import { api, qs } from './client.js';
import type {
  Paginated,
  RbOrder,
  RbOrderDetail,
  RbOrderUpdate,
  RbProduct,
  RbProductInput,
  RbProductOption,
  RbCategory,
  RbPrize,
  RbPrizeInput,
  RbCustomerPrizes,
} from './types.js';

/** 원본 randombox_category_modify.php 의 등록/수정 폼. server/shared/category-crud.ts 의 categoryBody 와 짝이다. */
export interface RbCategoryInput {
  companyName: string;
  companyCode: string;
  jpName?: string;
  /** 1 = FANZA, 2 = 바나나몰 */
  itemType?: string;
  memo?: string;
}

export interface RbOrderFilters {
  page?: number;
  perPage?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  q?: string;
  randomboxId?: number | '';
  orderStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  shipped?: 'yes' | 'no' | '';
  includeTest?: boolean;
}

export const randomboxApi = {
  orders: (filters: RbOrderFilters) =>
    api.get<Paginated<RbOrder>>(`/randombox/orders${qs({ ...filters })}`),

  order: (orderNo: string) =>
    api.get<RbOrderDetail>(`/randombox/orders/${encodeURIComponent(orderNo)}`),

  /** 원본 randombox_order_customer.php — 회원 한 명의 랜덤박스 당첨 이력. */
  customerPrizes: (memIdx: number) =>
    api.get<RbCustomerPrizes>(`/randombox/orders/customers/${memIdx}/prizes`),

  updateOrder: (orderNo: string, body: RbOrderUpdate) =>
    api.patch<{
      orderNo: string;
      updated: boolean;
      orderAffected: number;
      canceled: number;
      restored: number;
    }>(`/randombox/orders/${encodeURIComponent(orderNo)}`, body),

  products: (filters: {
    page?: number;
    perPage?: number;
    q?: string;
    sort?: string;
    dir?: string;
    categoryCode?: string;
    status?: string;
  }) => api.get<Paginated<RbProduct>>(`/randombox/products${qs({ ...filters })}`),

  productOptions: () => api.get<RbProductOption[]>('/randombox/products/options'),

  product: (randomboxId: number) => api.get<RbProduct>(`/randombox/products/${randomboxId}`),

  updateProduct: (randomboxId: number, body: RbProductInput) =>
    api.patch<{ id: number }>(`/randombox/products/${randomboxId}`, body),

  /** 원본 product_copy.php: 새 상품을 하나 만들고, 원본에 딸린 경품도 함께 복제한다. */
  copyProduct: (randomboxId: number, body: RbProductInput) =>
    api.post<{ id: number; copiedChildren: number; childCopyFailed?: boolean }>(
      `/randombox/products/${randomboxId}/copy`,
      body,
    ),

  categories: (filters: { page?: number; perPage?: number; q?: string; sort?: string; dir?: string }) =>
    api.get<Paginated<RbCategory>>(`/randombox/categories${qs({ ...filters })}`),

  /** 원본 randombox_category_modify.php (신규). server/shared/category-crud.ts 가 처리한다. */
  createCategory: (body: RbCategoryInput) =>
    api.post<{ seq: number }>('/randombox/categories', body),

  /** 원본 randombox_category_modify.php 의 modifyexe. server/shared/category-crud.ts 가 처리한다. */
  updateCategory: (seq: number, body: RbCategoryInput) =>
    api.patch<{ seq: number }>(`/randombox/categories/${seq}`, body),

  prizes: (randomboxId: number) =>
    api.get<{
      randomboxId: number;
      items: RbPrize[];
      totalQuantity: number;
      soldoutCount: number;
    }>(`/randombox/products/${randomboxId}/prizes`),

  /**
   * 경품 재고 수정. `field` 로 1번/2번 상품 재고 중 하나를 고른다 - 한 번에 한
   * 칸만 보내서 다른 칸의 값을 덮어쓰지 않는다(2026-10-02).
   */
  updatePrizeQuantity: (
    randomboxId: number,
    prizeId: number,
    quantity: number,
    field: 'quantity' | 'quantity2' = 'quantity',
  ) =>
    api.patch<{ previousQuantity?: number; changed: boolean; soldoutByStock?: boolean }>(
      `/randombox/products/${randomboxId}/prizes/${prizeId}/quantity`,
      { [field]: quantity },
    ),

  updatePrize: (randomboxId: number, prizeId: number, body: RbPrizeInput) =>
    api.patch<{ prizeId: number }>(`/randombox/products/${randomboxId}/prizes/${prizeId}`, body),

  /** 원본 prize_copy.php: 기존 경품 값을 미리 채운 폼으로 새 경품을 등록한다 — 등록과 같은 엔드포인트. */
  createPrize: (randomboxId: number, body: RbPrizeInput) =>
    api.post<{ prizeId: number }>(`/randombox/products/${randomboxId}/prizes`, body),

  deletePrize: (randomboxId: number, prizeId: number) =>
    api.del<{ prizeId: number; deleted: boolean }>(`/randombox/products/${randomboxId}/prizes/${prizeId}`),

  /**
   * 경품 이미지 제작 (원본 prize_list.php 의 '제작1'·'제작2').
   * 원본과 같이 만들어진 PNG 를 받아 바로 내려받는다 - 저장되는 것은 없다.
   *   rank = 제작1 (원본 make_thumbnail.php), display = 제작2 (원본 make_display.php)
   */
  makePrizeImage: (randomboxId: number, prizeId: number, kind: 'rank' | 'display') =>
    api.postBlob(`/randombox/products/${randomboxId}/prizes/${prizeId}/image/${kind}`, {}),

  // ── 배송 처리 (원본 randombox_excel_output* · delivery_excel_check*) ──────

  /** 비즈호스트 등록용 엑셀. 선택한 주문만 담긴다. */
  bizhostExportUrl: (seqs: number[]) =>
    `/api/randombox/delivery/bizhost-export${qs({ seqs: seqs.join(',') })}`,

  applyDelivery: (seqs: number[]) =>
    api.post<{ success: boolean; affected: number }>('/randombox/delivery/apply', { seqs }),

  stopDelivery: (seqs: number[]) =>
    api.post<{ success: boolean; affected: number }>('/randombox/delivery/stop', { seqs }),

  trackingPending: (randomboxId?: number | '') =>
    api.get<{ seq: number; hphone: string }[]>(
      `/randombox/delivery/tracking/pending${qs({ randomboxId })}`,
    ),

  applyTracking: (body: {
    randomboxId?: number | '';
    updates: { hphone: string; trackingNumber: string }[];
  }) =>
    api.post<{ success: boolean; updatedCount: number; totalUpdates: number }>(
      '/randombox/delivery/tracking/apply',
      body,
    ),
};
